import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { getToken, setToken, clearAllTokens } from "./api";
import { AuthorizationService } from "@/lib/api_client";
import {
  generatePKCEChallenge,
  setPKCEVerifier,
  getPKCEVerifier,
  clearPKCEVerifier,
  getRedirectUri,
  buildCriiptoAuthUrl,
  exchangeCodeForIDToken,
} from "./pkce";

const ROLES_KEY = "backoffice_roles";

function getStoredRoles(): string[] {
  try {
    const raw = localStorage.getItem(ROLES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function setStoredRoles(roles: string[]) {
  try {
    localStorage.setItem(ROLES_KEY, JSON.stringify(roles));
  } catch {}
}

// PR preview environments are served from a real hostname over TLS but still
// need dev login — BankID would require whitelisting every preview origin in
// Criipto. They opt in via BUN_PUBLIC_ALLOW_DEV_LOGIN (on by default in docker-compose.dev.yml).
function isDevMode(): boolean {
  // The counterpart to the frontend's `!__DEV__`: a production bundle must never
  // offer dev login, whatever the flag below says. build.ts pins
  // process.env.NODE_ENV to "production" via `define`, and Bun constant-folds
  // this comparison at bundle time — so in the production bundle the whole
  // function collapses to `return false` and the flag is not reachable code.
  // The dev server leaves NODE_ENV unset, so previews and localhost are
  // unaffected.
  if (process.env.NODE_ENV === "production") return false;
  if (process.env.BUN_PUBLIC_ALLOW_DEV_LOGIN === "true") return true;
  const hostname = window?.location?.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1";
}

interface AuthState {
  isLoggedIn: boolean;
  isLoggingIn: boolean;
  loginError: string | null;
  impersonatingUserId: string | null;
  impersonateToken: string | null;
  /** Grunnlag given for the impersonation in progress; null when not impersonating. */
  impersonateReason: string | null;
  roles: string[];
  hasRole: (role: string) => boolean;
  login: (idToken: string) => Promise<void>;
  loginWithBankID: () => Promise<void>;
  completeAuthFromCallback: (code: string) => Promise<void>;
  logout: () => void;
  impersonate: (userId: string, reason: string) => Promise<string>;
  stopImpersonating: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!getToken());
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [roles, setRoles] = useState<string[]>(() => getStoredRoles());
  const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(null);
  const [impersonateToken, setImpersonateToken] = useState<string | null>(null);
  const [impersonateReason, setImpersonateReason] = useState<string | null>(null);
  const [impersonateExpiresAt, setImpersonateExpiresAt] = useState<number | null>(null);

  const hasRole = useCallback((role: string) => roles.includes(role), [roles]);

  /** Mint admin token from an ID token (used by both dev login and OIDC callback) */
  const mintAndStore = useCallback(async (idToken: string) => {
    const res = await AuthorizationService.postApiAuthorizationMintAdminToken({ body: { idToken } });
    const data = res.data as unknown as { accessToken: string; roles?: string[] };
    if (!data?.accessToken) throw new Error("No access token returned");
    setToken(data.accessToken);
    const userRoles = data.roles ?? [];
    setStoredRoles(userRoles);
    setRoles(userRoles);
    setIsLoggedIn(true);
  }, []);

  /** Dev login: paste ID token or NIN directly */
  const login = useCallback(async (idToken: string) => {
    setIsLoggingIn(true);
    setLoginError(null);
    try {
      await mintAndStore(idToken);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Login failed";
      setLoginError(msg);
      throw err;
    } finally {
      setIsLoggingIn(false);
    }
  }, [mintAndStore]);

  /** Start BankID PKCE flow — redirects to Criipto */
  const loginWithBankID = useCallback(async () => {
    setIsLoggingIn(true);
    setLoginError(null);
    try {
      const { codeVerifier, codeChallenge } = await generatePKCEChallenge();
      const redirectUri = getRedirectUri();
      setPKCEVerifier(codeVerifier, redirectUri);
      const authUrl = buildCriiptoAuthUrl(codeChallenge, redirectUri);
      window.location.href = authUrl;
    } catch (err: unknown) {
      setIsLoggingIn(false);
      const msg = err instanceof Error ? err.message : "Could not start BankID login";
      setLoginError(msg);
    }
  }, []);

  /** Complete PKCE flow from /auth/callback */
  const completeAuthFromCallback = useCallback(async (code: string) => {
    setIsLoggingIn(true);
    setLoginError(null);
    try {
      const stored = getPKCEVerifier();
      if (!stored) throw new Error("PKCE verifier not found — please try logging in again");

      const idToken = await exchangeCodeForIDToken(code, stored.verifier, stored.redirectUri);
      clearPKCEVerifier();
      await mintAndStore(idToken);
    } catch (err: unknown) {
      clearPKCEVerifier();
      const msg = err instanceof Error ? err.message : "Login failed";
      setLoginError(msg);
      throw err;
    } finally {
      setIsLoggingIn(false);
    }
  }, [mintAndStore]);

  const logout = useCallback(() => {
    clearAllTokens();
    try { localStorage.removeItem(ROLES_KEY); } catch {}
    setIsLoggedIn(false);
    setImpersonatingUserId(null);
    setImpersonateToken(null);
    setImpersonateReason(null);
    setImpersonateExpiresAt(null);
    setRoles([]);
  }, []);

  // The reason is kept alongside the token so "Se som bruker" can reuse the
  // one the operator already gave for this patient rather than asking twice
  // for the same access.
  const impersonate = useCallback(async (userId: string, reason: string): Promise<string> => {
    const res = await AuthorizationService.postApiAuthorizationImpersonate({
      body: { userId, reason },
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const data = res.data as unknown as { accessToken: string; expiresIn: number };
    if (!data?.accessToken) {
      // The daily patient limit's 429 says what happened and who can lift it.
      const limitMessage =
        res.response?.status === 429 ? (res.error as { error?: string } | undefined)?.error : undefined;
      throw new Error(limitMessage ?? "Kunne ikke impersonere bruker");
    }
    setImpersonateToken(data.accessToken);
    setImpersonatingUserId(userId);
    setImpersonateReason(reason);
    setImpersonateExpiresAt(Date.now() + data.expiresIn * 1000);
    return data.accessToken;
  }, []);

  const stopImpersonating = useCallback(() => {
    setImpersonateToken(null);
    setImpersonatingUserId(null);
    setImpersonateReason(null);
    setImpersonateExpiresAt(null);
  }, []);

  // The token lasts an hour but a patient stays open as long as the page shows
  // them, and an expired token reads as anonymous, so the journal would go
  // silently empty. Re-mint for the same patient and grunnlag before it lapses:
  // each mint writes its own audit row, and reopening a patient is not counted
  // again toward the daily limit.
  useEffect(() => {
    if (!impersonateExpiresAt || !impersonatingUserId || !impersonateReason) return;
    const renew = setTimeout(() => {
      impersonate(impersonatingUserId, impersonateReason).catch(stopImpersonating);
    }, impersonateExpiresAt - Date.now() - 5 * 60_000);
    return () => clearTimeout(renew);
  }, [impersonateExpiresAt, impersonatingUserId, impersonateReason, impersonate, stopImpersonating]);

  useEffect(() => {
    const handleUnauthorized = () => logout();
    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", handleUnauthorized);
  }, [logout]);

  return (
    <AuthContext.Provider value={{ isLoggedIn, isLoggingIn, loginError, impersonatingUserId, impersonateToken, impersonateReason, roles, hasRole, login, loginWithBankID, completeAuthFromCallback, logout, impersonate, stopImpersonating }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { isDevMode };
